'use strict';

var FirebaseObject = require('./FirebaseObject');
var KayleeAuth = require('./KayleeAuth');
var KayleeBase = require('./KayleeBase');
var KayleeCache = require('./KayleeCache');
var KayleeUtil = require('./KayleeUtil');
var log = require('../log/Log').channel('KayleeEvent');

var KayleeEvent = {
    addUpdatesForCompanyChangedEvent: addUpdatesForCompanyChangedEvent,
    addUpdatesForCompanyCreatedEvent: addUpdatesForCompanyCreatedEvent,
    addUpdatesForCompanyDeletedEvent: addUpdatesForCompanyDeletedEvent,
    addUpdatesForControllerChangedEvent: addUpdatesForControllerChangedEvent,
    addUpdatesForControllerCreatedEvent: addUpdatesForControllerCreatedEvent,
    addUpdatesForControllerPairedEvent: addUpdatesForControllerPairedEvent,
    addUpdatesForControllerUnpairedEvent: addUpdatesForControllerUnpairedEvent,
    addUpdatesForEquipmentChangedEvent: addUpdatesForEquipmentChangedEvent,
    addUpdatesForEquipmentCreatedEvent: addUpdatesForEquipmentCreatedEvent,
    addUpdatesForEquipmentDeletedEvent: addUpdatesForEquipmentDeletedEvent,
    addUpdatesForRemoteControlAuthEvent: addUpdatesForRemoteControlAuthEvent,
    addUpdatesForRemoteControlDeletedEvent: addUpdatesForRemoteControlDeletedEvent,
    addUpdatesForRoleChangedEvent: addUpdatesForRoleChangedEvent,
    addUpdatesForRoleCreatedEvent: addUpdatesForRoleCreatedEvent,
    addUpdatesForRoleCreatedEventOnNewNode: addUpdatesForRoleCreatedEventOnNewNode,
    addUpdatesForRoleDeletedEvent: addUpdatesForRoleDeletedEvent,
    addUpdatesForRoomApiKeyCreatedEvent: addUpdatesForRoomApiKeyCreatedEvent,
    addUpdatesForRoomApiKeyDeletedEvent: addUpdatesForRoomApiKeyDeletedEvent,
    addUpdatesForRoomChangedEvent: addUpdatesForRoomChangedEvent,
    addUpdatesForRoomCreatedEvent: addUpdatesForRoomCreatedEvent,
    addUpdatesForRoomDeletedEvent: addUpdatesForRoomDeletedEvent,
    addUpdatesForSiteChangedEvent: addUpdatesForSiteChangedEvent,
    addUpdatesForSiteCreatedEvent: addUpdatesForSiteCreatedEvent,
    addUpdatesForSiteDeletedEvent: addUpdatesForSiteDeletedEvent,
    addUpdatesForUserChangedEvent: addUpdatesForUserChangedEvent,
    addUpdatesForUserCreatedEvent: addUpdatesForUserCreatedEvent,
    addUpdatesForUserDeletedEvent: addUpdatesForUserDeletedEvent,
    addUpdatesForUserPasswordChangedEvent: addUpdatesForUserPasswordChangedEvent,
    addUpdatesForUserPasswordResetEvent: addUpdatesForUserPasswordResetEvent,
    removeEvent: removeEvent,
    removeEvents: removeEvents,
    fromJson: fromJson,
    getEvent: getEvent,
    getEventIdFromUpdates: getEventIdFromUpdates,
};

function addUpdatesForCompanyChangedEvent(update, companyId, companyData, changed) {
    return addEvent(
        update,
        {
            object: 'company',
            action: 'changed',
            companyId: companyId,
        },
        true,
        null,
        companyData,
        changed
    );
}

function addUpdatesForCompanyCreatedEvent(update, companyId, companyName) {
    return addEvent(
        update,
        {
            object: 'company',
            action: 'created',
            companyId: companyId,
            companyName: companyName,
        },
        true
    );
}

function addUpdatesForCompanyDeletedEvent(update, companyId) {
    return addEvent(
        update,
        {
            object: 'company',
            action: 'deleted',
            companyId: companyId,
        },
        true
    );
}

function addUpdatesForControllerChangedEvent(
    update,
    controllerId,
    controllerData,
    changed,
    userInitiated
) {
    var event = {
        object: 'controller',
        action: 'changed',
        controllerId: controllerId,
        serialNumber: controllerData.serialNumber,
    };

    if (controllerData.hasOwnProperty('companyId')) {
        event.companyId = controllerData.companyId;
        event.siteId = controllerData.siteId;
        event.roomId = controllerData.roomId;
    }

    return addEvent(update, event, userInitiated, null, controllerData, changed);
}

function addUpdatesForControllerCreatedEvent(update, controllerId, serialNumber) {
    return addEvent(
        update,
        {
            object: 'controller',
            action: 'created',
            controllerId: controllerId,
            serialNumber: serialNumber,
        },
        false
    );
}

function addUpdatesForControllerPairedEvent(update, controllerId, roomId, actorUserId) {
    var obj = { roomId: roomId, controllerId: controllerId };
    if (actorUserId) obj.actorUserId = actorUserId;

    return KayleeUtil.loadRoomData(obj)
        .then(KayleeUtil.loadControllerData)
        .then(function (obj) {
            if (actorUserId) return KayleeUtil.loadActorData(obj);
            return obj;
        })
        .then(function (obj) {
            var event = {
                object: 'controller',
                action: 'paired',
                controllerId: controllerId,
                serialNumber: obj.controller.serialNumber,
                companyId: obj.room.companyId,
                siteId: obj.room.siteId,
                roomId: roomId,
            };

            if (actorUserId) {
                // Small hack here. This is a user-initiated event, but Jayne is
                // writing the event. So, manually stick in the actor info here,
                // and tell addEvent that it's not user-initiated.
                event.actorUserId = obj.actorUserId;
                event.actorFirstName = obj.actor.firstName;
                event.actorLastName = obj.actor.lastName;
                event.actorEmail = obj.actor.email;
            }

            return addEvent(update, event, false);
        });
}

function addUpdatesForControllerUnpairedEvent(update, controllerId, parentEventId) {
    return KayleeUtil.loadControllerData({ controllerId: controllerId }).then(function (obj) {
        return addEvent(
            update,
            {
                object: 'controller',
                action: 'unpaired',
                controllerId: controllerId,
                serialNumber: obj.controller.serialNumber,
                companyId: obj.controller.companyId,
                siteId: obj.controller.siteId,
                roomId: obj.controller.roomId,
            },
            KayleeAuth.isAuthenticatedAsUser(),
            parentEventId
        );
    });
}

function addUpdatesForEquipmentChangedEvent(
    update,
    equipmentId,
    equipmentData,
    changed,
    userInitiated
) {
    var event = {
        object: 'equipment',
        action: 'changed',
        equipmentId: equipmentId,
        type: equipmentData.type,
        ipAddress: equipmentData.ipAddress,
        companyId: equipmentData.companyId,
        siteId: equipmentData.siteId,
        roomId: equipmentData.roomId,
    };

    if (typeof equipmentData.modelNumber !== 'undefined') {
        event.modelNumber = equipmentData.modelNumber;
    }

    return addEvent(update, event, userInitiated, null, equipmentData, changed);
}

function addUpdatesForEquipmentCreatedEvent(
    update,
    roomId,
    equipmentId,
    type,
    modelNumber,
    ipAddress
) {
    return KayleeUtil.loadRoomData({ roomId: roomId }).then(function (obj) {
        var event = {
            object: 'equipment',
            action: 'created',
            equipmentId: equipmentId,
            type: type,
            ipAddress: ipAddress,
            companyId: obj.room.companyId,
            siteId: obj.room.siteId,
            roomId: roomId,
        };

        if (typeof modelNumber !== 'undefined') event.modelNumber = modelNumber;

        return addEvent(update, event, true);
    });
}

function addUpdatesForEquipmentDeletedEvent(update, equipmentId, parentEventId) {
    return KayleeUtil.loadEquipmentData({ equipmentId: equipmentId }).then(function (obj) {
        var event = {
            object: 'equipment',
            action: 'deleted',
            equipmentId: equipmentId,
            type: obj.equipment.type,
            ipAddress: obj.equipment.ipAddress,
            companyId: obj.equipment.companyId,
            siteId: obj.equipment.siteId,
            roomId: obj.equipment.roomId,
        };

        if (typeof obj.equipment.modelNumber !== 'undefined') {
            event.modelNumber = obj.equipment.modelNumber;
        }

        return addEvent(update, event, true, parentEventId);
    });
}

function addUpdatesForRemoteControlAuthEvent(
    update,
    userId,
    ipAddress,
    userAgent,
    result,
    companyId,
    siteId,
    roomId
) {
    var event = {
        object: 'remoteControl',
        action: 'auth',
        ipAddress: ipAddress,
        result: result,
        userId: userId,
    };

    if (userAgent) event.userAgent = userAgent;
    if (companyId) event.companyId = companyId;
    if (siteId) event.siteId = siteId;
    if (roomId) event.roomId = roomId;

    return addEvent(update, event, false);
}

function addUpdatesForRemoteControlDeletedEvent(
    update,
    result,
    actorUserId,
    companyId,
    siteId,
    roomId,
    userId,
    ipAddress,
    userAgent
) {
    var event = {
        object: 'remoteControl',
        action: 'deleted',
        result: result,
    };

    if (companyId) event.companyId = companyId;
    if (siteId) event.siteId = siteId;
    if (roomId) event.roomId = roomId;
    if (userId) event.userId = userId;
    if (ipAddress) event.ipAddress = ipAddress;
    if (userAgent) event.userAgent = userAgent;

    // Small hack here. This is a user-initiated event, but Jayne is writing
    // the event. So, manually stick in the actor info here, and tell addEvent
    // that it's not user-initiated.

    if (typeof actorUserId !== 'undefined') event.actorUserId = actorUserId;

    return Promise.resolve()
        .then(function () {
            if (
                typeof actorUserId !== 'undefined' &&
                actorUserId !== 'anonymousRemoteControl' &&
                !KayleeBase.isInaraUserId(actorUserId)
            ) {
                return KayleeUtil.loadActorData({ actorUserId: actorUserId }).then(function (obj) {
                    event.actorFirstName = obj.actor.firstName;
                    event.actorLastName = obj.actor.lastName;
                    event.actorEmail = obj.actor.email;
                });
            }
        })
        .then(function () {
            return addEvent(update, event, false);
        });
}

function addUpdatesForRoleChangedEvent(
    update,
    userId,
    prevRole,
    newRole,
    companyId,
    siteId,
    roomId,
    actorUserId
) {
    return KayleeUtil.loadActorData({ actorUserId: actorUserId }).then(function (obj) {
        var event = {
            object: 'role',
            action: 'changed',
            userId: userId,
            role: prevRole,
            changed: {
                role: newRole,
            },

            // Small hack here. This is a user-initiated event, but usually
            // Jayne is writing the event. So, manually stick in the actor info
            // here, and tell addEvent that it's not user-initiated.

            actorUserId: obj.actorUserId,
            actorFirstName: obj.actor.firstName,
            actorLastName: obj.actor.lastName,
            actorEmail: obj.actor.email,
        };

        if (typeof companyId !== 'undefined' && companyId !== null) {
            event.companyId = companyId;
        }

        if (typeof siteId !== 'undefined' && siteId !== null) {
            event.siteId = siteId;
        }

        if (typeof roomId !== 'undefined' && roomId !== null) {
            event.roomId = roomId;
        }

        return addEvent(update, event, false);
    });
}

function addUpdatesForRoleCreatedEvent(
    update,
    userId,
    role,
    companyId,
    siteId,
    roomId,
    actorUserId
) {
    var event = {
        object: 'role',
        action: 'created',
        userId: userId,
        role: role,
    };

    return Promise.resolve()
        .then(function () {
            if (actorUserId) {
                return KayleeUtil.loadActorData({ actorUserId: actorUserId }).then(function (obj) {
                    // Small hack here. This is a user-initiated event, but usually
                    // Jayne is writing the event. So, manually stick in the actor info
                    // here, and tell addEvent that it's not user-initiated.

                    event.actorUserId = obj.actorUserId;
                    event.actorFirstName = obj.actor.firstName;
                    event.actorLastName = obj.actor.lastName;
                    event.actorEmail = obj.actor.email;

                    return obj;
                });
            }

            return {};
        })
        .then(function (obj) {
            if (typeof companyId !== 'undefined' && companyId !== null) {
                event.companyId = companyId;
            }

            if (typeof siteId !== 'undefined' && siteId !== null) {
                event.siteId = siteId;
            }

            if (typeof roomId !== 'undefined' && roomId !== null) {
                event.roomId = roomId;
            }

            return addEvent(update, event, false);
        });
}

function addUpdatesForRoleCreatedEventOnNewNode(
    update,
    userId,
    role,
    parentEventId,
    companyId,
    companyName,
    siteId,
    siteName,
    roomId,
    roomName
) {
    var event = {
        object: 'role',
        action: 'created',
        userId: userId,
        role: role,
    };

    if (typeof companyId !== 'undefined') {
        event.companyId = companyId;

        if (typeof companyName !== 'undefined' && companyName !== null) {
            event.companyName = companyName;
        }

        if (typeof siteId !== 'undefined') {
            event.siteId = siteId;

            if (typeof siteName !== 'undefined' && siteName !== null) {
                event.siteName = siteName;
            }

            if (typeof roomId !== 'undefined') {
                event.roomId = roomId;

                if (typeof roomName !== 'undefined' && roomName !== null) {
                    event.roomName = roomName;
                }
            }
        }
    }

    return addEvent(update, event, true, parentEventId);
}

function addUpdatesForRoleDeletedEvent(
    update,
    userId,
    role,
    companyId,
    siteId,
    roomId,
    actorUserId,
    parentEventId
) {
    var event = {
        object: 'role',
        action: 'deleted',
        userId: userId,
        role: role,
    };

    return Promise.resolve()
        .then(function () {
            if (actorUserId) {
                return KayleeUtil.loadActorData({ actorUserId: actorUserId }).then(function (obj) {
                    // Small hack here. This is a user-initiated event, but usually
                    // Jayne is writing the event. So, manually stick in the actor info
                    // here, and tell addEvent that it's not user-initiated.

                    event.actorUserId = obj.actorUserId;
                    event.actorFirstName = obj.actor.firstName;
                    event.actorLastName = obj.actor.lastName;
                    event.actorEmail = obj.actor.email;

                    return obj;
                });
            }

            return {};
        })
        .then(function (obj) {
            if (typeof companyId !== 'undefined' && companyId !== null) {
                event.companyId = companyId;
            }

            if (typeof siteId !== 'undefined' && siteId !== null) {
                event.siteId = siteId;
            }

            if (typeof roomId !== 'undefined' && roomId !== null) {
                event.roomId = roomId;
            }

            return addEvent(update, event, false, parentEventId);
        });
}

function addUpdatesForRoomApiKeyCreatedEvent(update, roomId, apiKey, description) {
    return KayleeUtil.loadRoomData({ roomId: roomId }).then(function (obj) {
        var event = {
            object: 'roomApiKey',
            action: 'created',
            companyId: obj.room.companyId,
            siteId: obj.room.siteId,
            roomId: roomId,
            apiKey: apiKey,
        };

        if (description) event.description = description;

        return addEvent(update, event, true);
    });
}

function addUpdatesForRoomApiKeyDeletedEvent(update, roomId, apiKey, description) {
    return KayleeUtil.loadRoomData({ roomId: roomId }).then(function (obj) {
        var event = {
            object: 'roomApiKey',
            action: 'deleted',
            companyId: obj.room.companyId,
            siteId: obj.room.siteId,
            roomId: roomId,
            apiKey: apiKey,
        };

        if (description) event.description = description;

        return addEvent(update, event, true);
    });
}

function addUpdatesForRoomChangedEvent(update, roomId, roomData, changed, userInitiated) {
    return addEvent(
        update,
        {
            object: 'room',
            action: 'changed',
            companyId: roomData.companyId,
            siteId: roomData.siteId,
            roomId: roomId,
        },
        userInitiated,
        null,
        roomData,
        changed
    );
}

function addUpdatesForRoomCreatedEvent(update, siteId, roomId, roomName) {
    return KayleeUtil.loadSiteData({ siteId: siteId }).then(function (obj) {
        return addEvent(
            update,
            {
                object: 'room',
                action: 'created',
                companyId: obj.site.companyId,
                siteId: siteId,
                roomId: roomId,
                roomName: roomName,
            },
            true
        );
    });
}

function addUpdatesForRoomDeletedEvent(update, roomId, parentEventId) {
    return KayleeUtil.loadRoomData({ roomId: roomId }).then(function (obj) {
        return addEvent(
            update,
            {
                object: 'room',
                action: 'deleted',
                companyId: obj.room.companyId,
                siteId: obj.room.siteId,
                roomId: roomId,
            },
            true,
            parentEventId
        );
    });
}

function addUpdatesForSiteChangedEvent(update, siteId, siteData, changed) {
    return addEvent(
        update,
        {
            object: 'site',
            action: 'changed',
            companyId: siteData.companyId,
            siteId: siteId,
        },
        true,
        null,
        siteData,
        changed
    );
}

function addUpdatesForSiteCreatedEvent(update, companyId, siteId, siteName) {
    return addEvent(
        update,
        {
            object: 'site',
            action: 'created',
            companyId: companyId,
            siteId: siteId,
            siteName: siteName,
        },
        true
    );
}

function addUpdatesForSiteDeletedEvent(update, siteId, parentEventId) {
    return KayleeUtil.loadSiteData({ siteId: siteId }).then(function (obj) {
        return addEvent(
            update,
            {
                object: 'site',
                action: 'deleted',
                companyId: obj.site.companyId,
                siteId: siteId,
            },
            true,
            parentEventId
        );
    });
}

function addUpdatesForUserChangedEvent(update, userId, userData, changed) {
    return addEvent(
        update,
        {
            object: 'user',
            action: 'changed',
            userId: userId,
            firstName: userData.firstName,
            lastName: userData.lastName,
            email: userData.email,
        },
        true,
        null,
        userData,
        changed
    );
}

function addUpdatesForUserCreatedEvent(update, userId, firstName, lastName, email) {
    return addEvent(
        update,
        {
            object: 'user',
            action: 'created',
            userId: userId,
            firstName: firstName,
            lastName: lastName,
            email: email,
        },
        false
    );
}

function addUpdatesForUserDeletedEvent(update, userId) {
    var userInitiated = KayleeBase.app.auth().currentUser ? true : false;

    return KayleeUtil.loadUserData({ userId: userId }).then(function (obj) {
        return addEvent(
            update,
            {
                object: 'user',
                action: 'deleted',
                userId: userId,
                firstName: obj.user.firstName,
                lastName: obj.user.lastName,
                email: obj.user.email,
            },
            userInitiated
        );
    });
}

function addUpdatesForUserPasswordChangedEvent(update, userId) {
    return KayleeUtil.loadUserData({ userId: userId }).then(function (obj) {
        return addEvent(
            update,
            {
                object: 'user',
                action: 'passwordChanged',
                userId: userId,
                firstName: obj.user.firstName,
                lastName: obj.user.lastName,
                email: obj.user.email,
            },
            true
        );
    });
}

function addUpdatesForUserPasswordResetEvent(update, userId) {
    return KayleeUtil.loadUserData({ userId: userId }).then(function (obj) {
        return addEvent(
            update,
            {
                object: 'user',
                action: 'passwordReset',
                userId: userId,
                firstName: obj.user.firstName,
                lastName: obj.user.lastName,
                email: obj.user.email,
            },
            true
        );
    });
}

function addEvent(update, event, userInitiated, parentEventId, nodeData, newProps) {
    var eventId = KayleeBase.app.database().ref().push().key;

    log.debug('adding ' + event.object + ' ' + event.action + ' event (' + eventId + ')');

    // Load any required data and then build the update.

    return Promise.resolve({})
        .then(function (obj) {
            if (
                (typeof userInitiated !== 'undefined' && !userInitiated) ||
                !KayleeBase.app.auth().currentUser
            )
                return obj;

            obj.actorUserId = KayleeBase.app.auth().currentUser.uid;
            return KayleeUtil.loadActorData(obj, false).then(
                function (obj) {
                    if (obj.actor === null) {
                        log.error('actor data is null, maybe you meant to use $save(false)');
                        return obj;
                    }

                    event.actorUserId = obj.actorUserId;
                    event.actorFirstName = obj.actor.firstName;
                    event.actorLastName = obj.actor.lastName;
                    event.actorEmail = obj.actor.email;
                    return obj;
                },
                function (err) {
                    log.error(
                        'failed loading actor data, ' +
                            'maybe you meant to use $save(false), error: ' +
                            err
                    );
                }
            );
        })
        .then(function (obj) {
            if (event.hasOwnProperty('userId') && !event.hasOwnProperty('firstName')) {
                obj.userId = event.userId;
                return KayleeUtil.loadUserData(obj).then(function (obj) {
                    event.userFirstName = obj.user.firstName;
                    event.userLastName = obj.user.lastName;
                    event.userEmail = obj.user.email;
                    return obj;
                });
            }

            return obj;
        })
        .then(function (obj) {
            if (event.hasOwnProperty('controllerId') && !event.hasOwnProperty('serialNumber')) {
                obj.controllerId = event.controllerId;
                return KayleeUtil.loadControllerData(obj).then(function (obj) {
                    event.serialNumber = obj.controller.serialNumber;
                    return obj;
                });
            }

            return obj;
        })
        .then(function (obj) {
            if (event.hasOwnProperty('companyId') && !event.hasOwnProperty('companyName')) {
                obj.companyId = event.companyId;
                return KayleeUtil.loadCompanyData(obj).then(function (obj) {
                    event.companyName = obj.company.name;
                    return obj;
                });
            }

            return obj;
        })
        .then(function (obj) {
            if (event.hasOwnProperty('siteId') && !event.hasOwnProperty('siteName')) {
                obj.siteId = event.siteId;
                return KayleeUtil.loadSiteData(obj).then(function (obj) {
                    event.siteName = obj.site.name;
                    return obj;
                });
            }

            return obj;
        })
        .then(function (obj) {
            if (event.hasOwnProperty('roomId') && !event.hasOwnProperty('roomName')) {
                obj.roomId = event.roomId;
                return KayleeUtil.loadRoomData(obj).then(function (obj) {
                    event.roomName = obj.room.name;
                    return obj;
                });
            }

            return obj;
        })
        .then(function (obj) {
            // Add the time to the event.
            event.time = Date.now();

            // Add the parentEventId if necessary.

            if (typeof parentEventId !== 'undefined' && parentEventId !== null) {
                event.parentEventId = parentEventId;
            }

            // If there are new values, add them to the event and also add the
            // appropriate previous values to the event.

            if (typeof newProps !== 'undefined' && newProps !== null) {
                addProps(event, nodeData, newProps);
                event.changed = newProps;
            }

            // Add the event to the appropriate indices.

            if (event.hasOwnProperty('userId')) {
                update['index/eventsByUser/' + event.userId + '/' + eventId] = true;
            }

            if (event.hasOwnProperty('companyId')) {
                update['index/eventsByCompany/' + event.companyId + '/' + eventId] = true;
            }

            if (event.hasOwnProperty('siteId')) {
                update['index/eventsBySite/' + event.siteId + '/' + eventId] = true;
            }

            if (event.hasOwnProperty('roomId')) {
                update['index/eventsByRoom/' + event.roomId + '/' + eventId] = true;
            }

            if (event.hasOwnProperty('controllerId')) {
                update['index/eventsByController/' + event.controllerId + '/' + eventId] = true;
            }

            if (event.hasOwnProperty('equipmentId')) {
                update['index/eventsByEquipment/' + event.equipmentId + '/' + eventId] = true;
            }

            update['events/' + eventId] = event;
            return update;
        });
}

function addProps(event, nodeData, newProps, depth) {
    if (typeof depth === 'undefined') depth = 0;

    for (var prop in newProps) {
        if (!newProps.hasOwnProperty(prop)) continue;

        // If this is the top-level name property for a company, site, or room,
        // then skip it because the top-level property will actually be
        // companyName, siteName, or roomName, and it is required to be there.

        if (
            depth === 0 &&
            prop === 'name' &&
            (event.object === 'company' || event.object === 'site' || event.object === 'room')
        ) {
            continue;
        }

        // When configuration items have been removed, a special property
        // named 'removed' is added. Don't include it in the top-level
        // properties.

        if (prop === 'removed') {
            continue;
        }

        if (newProps[prop] !== null && typeof newProps[prop] === 'object') {
            if (nodeData.hasOwnProperty(prop)) {
                if (!event.hasOwnProperty(prop)) event[prop] = {};
                addProps(event[prop], nodeData[prop], newProps[prop], depth + 1);
                if (Object.keys(event[prop]).length <= 0) delete event[prop];
            }
        } else {
            if (!event.hasOwnProperty(prop) && nodeData.hasOwnProperty(prop)) {
                event[prop] = nodeData[prop];
            }
        }
    }
}

function fromJson(data) {
    var reqProps = ['$id'];
    return FirebaseObject.$fromJson(data, 'Event', reqProps, reqProps, function (data) {
        return getEvent(data.$id);
    });
}

function getEventIdFromUpdates(updates) {
    var re = /^events\/(.+)/;
    var match;

    for (var prop in updates) {
        if (!updates.hasOwnProperty(prop)) continue;

        match = re.exec(prop);
        if (match !== null) return match[1];
    }

    return null;
}

function getEvent(eventId) {
    var key = 'Event: ' + eventId;
    var event = KayleeCache.get(key);

    if (event === null) {
        event = new Event(eventId);
        KayleeCache.put(key, event);
    }

    return event;
}

function removeEvent(eventId) {
    return KayleeUtil.load({ eventId: eventId }).then(function (obj) {
        var event = obj.event;
        var updates = {};

        if (event.hasOwnProperty('billingAccountId')) {
            updates['eventsByBillingAccount/' + event.billingAccountId + '/' + eventId] = null;
        }

        if (event.hasOwnProperty('companyId')) {
            updates['eventsByCompany/' + event.companyId + '/' + eventId] = null;
        }

        if (event.hasOwnProperty('distributorId')) {
            updates['eventsByDistributor/' + event.distributorId + '/' + eventId] = null;
        }

        if (event.hasOwnProperty('siteId')) {
            updates['eventsBySite/' + event.siteId + '/' + eventId] = null;
        }

        if (event.hasOwnProperty('roomId')) {
            updates['eventsByRoom/' + event.roomId + '/' + eventId] = null;
        }

        if (event.hasOwnProperty('controllerId')) {
            updates['eventsByController/' + event.controllerId + '/' + eventId] = null;
        }

        if (event.hasOwnProperty('equipmentId')) {
            updates['eventsByEquipment/' + event.equipmentId + '/' + eventId] = null;
        }

        if (event.hasOwnProperty('userId')) {
            updates['eventsByUser/' + event.userId + '/' + eventId] = null;
        }

        if (event.hasOwnProperty('voucherId')) {
            updates['eventsByVoucher/' + event.voucherId + '/' + eventId] = null;
        }

        return KayleeBase.app
            .database()
            .ref('events/' + eventId)
            .set(null)
            .then(function () {
                if (Object.keys(updates).length > 0) {
                    return KayleeBase.app.database().ref('index').update(updates);
                }
            });
    });
}

function removeEvents(events) {
    var eventUpdates = {};
    var indexUpdates = {};

    for (var eventId in events) {
        if (!events.hasOwnProperty(eventId)) continue;

        var event = events[eventId];

        eventUpdates[eventId] = null;

        if (event.hasOwnProperty('billingAccountId')) {
            indexUpdates['eventsByBillingAccount/' + event.billingAccountId + '/' + eventId] = null;
        }

        if (event.hasOwnProperty('companyId')) {
            indexUpdates['eventsByCompany/' + event.companyId + '/' + eventId] = null;
        }

        if (event.hasOwnProperty('distributorId')) {
            indexUpdates['eventsByDistributor/' + event.distributorId + '/' + eventId] = null;
        }

        if (event.hasOwnProperty('siteId')) {
            indexUpdates['eventsBySite/' + event.siteId + '/' + eventId] = null;
        }

        if (event.hasOwnProperty('roomId')) {
            indexUpdates['eventsByRoom/' + event.roomId + '/' + eventId] = null;
        }

        if (event.hasOwnProperty('controlerId')) {
            indexUpdates['eventsByController/' + event.controllerId + '/' + eventId] = null;
        }

        if (event.hasOwnProperty('equipmentId')) {
            indexUpdates['eventsByEquipment/' + event.equipentId + '/' + eventId] = null;
        }

        if (event.hasOwnProperty('userId')) {
            indexUpdates['eventsByUser/' + event.userId + '/' + eventId] = null;
        }

        if (event.hasOwnProperty('voucherId')) {
            indexUpdates['eventsByVoucher/' + event.voucherId + '/' + eventId] = null;
        }
    }

    return KayleeBase.app
        .database()
        .ref('events')
        .update(eventUpdates)
        .then(function () {
            if (Object.keys(indexUpdates).length > 0) {
                return KayleeBase.app.database().ref('index').update(indexUpdates);
            }
        });
}

function Event(eventId) {
    FirebaseObject.call(this, KayleeBase.app.database().ref('events/' + eventId));
}

Event.prototype = Object.create(FirebaseObject.prototype);
Event.prototype.constructor = Event;

Event.prototype.$toJson = function () {
    return FirebaseObject.prototype.$toJson.call(this, ['$id']);
};

Event.prototype.toString = function () {
    return '[Event (' + this.$id + ')]';
};

module.exports = KayleeEvent;
